// DagVersionsPanel — the VERSIONING views from the /dag_design prototype.
//
// Ported from the dc-runtime prototype's `verView` sub-screen (component.js:
// gitTree / gitAncestors / gitDiff / gitMerge / versionsFor + the renderVals
// `gitView` / `selVersions` / `verBtns` blocks). It realises the three
// versioning views the design specifies:
//
//   • 'artifact' — a selected artifact's linear VERSION HISTORY timeline
//                  (re-runs create versions; the current version may lag its
//                  inputs → "stale").
//   • 'db'       — the whole-DB DATABASE TREE: a git-style branch/commit graph
//                  with lanes per branch, a commit-compare A/B picker, the
//                  commit diff readout, and a synthesized MERGE PREVIEW.
//   • 'graph'    — the on-DAG note (the explorer draws version badges on the
//                  graph itself; here we render the caption that pairs with it).
//
// DATA: Thread has a real CoW versioning layer (branches + commits). When the
// caller supplies that real data via `artifactVersions` / `branchHistory`, we
// render it. Otherwise we fall back to the prototype's DETERMINISTIC-FROM-ID
// presentation so the VIEWS + interactions match the design exactly (the user
// wants to SEE these views regardless of whether the backend is wired yet).
//
// Colours come from --v5-* design tokens (thread-v5.css) — never hard-coded.
// This component is PURE on its props; it never fetches.

import { useCallback, useMemo, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/** Versions sub-screen, mirrors the prototype's `verView`. */
export type VerView = "graph" | "db" | "artifact";

/** One version of a single artifact (a re-run produces a new version). */
export interface ArtifactVersion {
  /** Short label, e.g. "v1", "v2". */
  v: string;
  /** Human date, e.g. "May 14". */
  date: string;
  /** Whether this is the current HEAD version. */
  current?: boolean;
}

/** A branch lane in the database tree. */
export interface VersionBranch {
  id: string;
  /** 0-based lane index (vertical position in the tree). */
  lane: number;
  /** Whether this branch has been merged into main. */
  merged: boolean;
  /** Optional explicit colour (a --v5-* var); defaults by lane. */
  color?: string;
}

/** A commit dot in the database tree. */
export interface VersionCommit {
  hash: string;
  lane: number;
  /** Horizontal sequence index (column). */
  seq: number;
  /** Parent commit hashes (1 = normal, 2 = merge). */
  parents: string[];
  name: string;
  date: string;
  /** Optional tag, e.g. "only in coarse". */
  tag?: string;
  /** Whether this is the current HEAD of its branch. */
  current?: boolean;
}

/** A full branch/commit history (the real CoW data, when available). */
export interface BranchHistory {
  branches: VersionBranch[];
  commits: VersionCommit[];
}

export interface DagVersionsPanelProps {
  /** The selected artifact id (drives the deterministic fallback + titles). */
  artifactId: string;
  /** Optional human label for the selected artifact (defaults to the id). */
  artifactLabel?: string;
  /**
   * Real CoW version history for the selected artifact. When omitted, a
   * deterministic-from-id history is synthesized so the view still renders.
   */
  artifactVersions?: ArtifactVersion[];
  /**
   * Real CoW branch/commit history (the whole-DB tree). When omitted, the
   * prototype's deterministic tree is used.
   */
  branchHistory?: BranchHistory;
  /** Active sub-view. Controlled when `onViewChange` is provided. */
  view?: VerView;
  onViewChange?: (view: VerView) => void;
  /** Initial sub-view when uncontrolled. Default 'artifact'. */
  defaultView?: VerView;
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Deterministic fallbacks (ported verbatim from the prototype) — used only when
// real CoW data is not supplied via props.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deterministic version history for an artifact: re-runs create versions, and
 * the current one may lag its inputs ("stale"). Ported from
 * component.js::versionsFor.
 */
export function deterministicVersionsFor(id: string): {
  versions: ArtifactVersion[];
  stale: boolean;
  inputChanged: string | null;
} {
  const seed = [...id].reduce((a, c) => a + c.charCodeAt(0), 0);
  const stale = seed % 11 === 5;
  const n = 2 + (seed % 3); // 2–4 versions
  const versions: ArtifactVersion[] = [];
  for (let i = 1; i <= n; i += 1) {
    const day = 3 + ((seed * i) % 22);
    versions.push({ v: `v${i}`, date: `May ${day}`, current: i === n });
  }
  const inputChanged = stale ? `May ${3 + ((seed * n) % 22) + 4}` : null;
  return { versions, stale, inputChanged };
}

/** The prototype's deterministic whole-DB branch/commit tree (gitTree). */
export function deterministicBranchHistory(): BranchHistory {
  const branches: VersionBranch[] = [
    { id: "main", lane: 0, color: "var(--v5-ink)", merged: true },
    { id: "high-Re", lane: 1, color: "var(--v5-blue)", merged: true },
    { id: "coarse", lane: 2, color: "var(--v5-amber)", merged: false },
  ];
  const commits: VersionCommit[] = [
    { hash: "a1b2c3d", lane: 0, seq: 0, parents: [], name: "initial fit", date: "May 3" },
    { hash: "4e5f6a7", lane: 0, seq: 1, parents: ["a1b2c3d"], name: "add transonic", date: "May 8" },
    { hash: "9c0d1e2", lane: 2, seq: 2, parents: ["a1b2c3d"], name: "coarse baseline", date: "May 6", tag: "only in coarse" },
    { hash: "8b9c0d1", lane: 1, seq: 2, parents: ["4e5f6a7"], name: "high-Re sweep", date: "May 11" },
    { hash: "5a6b7c8", lane: 1, seq: 3, parents: ["8b9c0d1"], name: "refit α>12", date: "May 14" },
    { hash: "2e3f4a5", lane: 0, seq: 4, parents: ["4e5f6a7", "5a6b7c8"], name: "merge high-Re", date: "May 16", current: true },
  ];
  return { branches, commits };
}

/** A lane's default colour when a branch supplies none. */
const LANE_COLORS = ["var(--v5-ink)", "var(--v5-blue)", "var(--v5-amber)", "var(--v5-primary)"];
const colorOfBranch = (b: VersionBranch): string => b.color ?? LANE_COLORS[b.lane % LANE_COLORS.length]!;

// ─────────────────────────────────────────────────────────────────────────────
// Git lineage maths (ported from gitAncestors / gitDiff / gitMerge).
// These operate on whatever BranchHistory we have — real or deterministic.
// ─────────────────────────────────────────────────────────────────────────────

/** All ancestor hashes of a commit (exclusive of itself). */
export function commitAncestors(history: BranchHistory, hash: string): Set<string> {
  const byHash = new Map(history.commits.map((c) => [c.hash, c] as const));
  const seen = new Set<string>();
  const stack = [hash];
  while (stack.length) {
    const h = stack.pop()!;
    const c = byHash.get(h);
    if (!c) continue;
    for (const p of c.parents) {
      if (!seen.has(p)) {
        seen.add(p);
        stack.push(p);
      }
    }
  }
  return seen;
}

export interface CommitDiff {
  rel: string;
  compat: "compatible" | "needs merge";
  shared: number;
  aOnly: number;
  bOnly: number;
  base: string;
  baseName: string;
  aName: string;
  bName: string;
}

/** Compare two commits: same line or diverged? what's shared / unique? */
export function commitDiff(history: BranchHistory, a: string | null, b: string | null): CommitDiff | null {
  if (!a || !b) return null;
  const byHash = new Map(history.commits.map((c) => [c.hash, c] as const));
  const ancA = new Set<string>([a, ...commitAncestors(history, a)]);
  const ancB = new Set<string>([b, ...commitAncestors(history, b)]);
  const shared = [...ancA].filter((h) => ancB.has(h));
  const aOnly = [...ancA].filter((h) => !ancB.has(h));
  const bOnly = [...ancB].filter((h) => !ancA.has(h));
  let rel: string;
  let compat: CommitDiff["compat"];
  if (ancB.has(a)) {
    rel = "B builds on A — A is an ancestor of B";
    compat = "compatible";
  } else if (ancA.has(b)) {
    rel = "A builds on B — B is an ancestor of A";
    compat = "compatible";
  } else {
    rel = "diverged — neither contains the other";
    compat = "needs merge";
  }
  const base = shared
    .map((h) => byHash.get(h))
    .filter((c): c is VersionCommit => !!c)
    .sort((x, y) => y.seq - x.seq)[0];
  return {
    rel,
    compat,
    shared: shared.length,
    aOnly: aOnly.length,
    bOnly: bOnly.length,
    base: base ? base.hash : "—",
    baseName: base ? base.name : "—",
    aName: byHash.get(a)?.name ?? a,
    bName: byHash.get(b)?.name ?? b,
  };
}

/** Short deterministic FNV-1a hash for a synthesized merge commit. */
function hashFor(id: string): string {
  let h = 0x811c9dc5;
  const s = `thread:${id}`;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `0000000${(h >>> 0).toString(16)}`.slice(-7);
}

export interface MergePreview {
  ff: boolean;
  span: number;
  conflicts: string[];
  nConf: number;
  mergeHash: string;
  verdict: string;
}

/** Synthesized merge of two commits: span + conflict set (gitMerge). */
export function mergePreview(history: BranchHistory, a: string | null, b: string | null): MergePreview | null {
  const d = commitDiff(history, a, b);
  if (!d || !a || !b) return null;
  const ff = d.compat === "compatible";
  const pool = ["drag-polar fit", "C_m,α derivative", "reference area", "transition trip", "damping model"];
  const seed = [...a, ...b].reduce((s, c) => s + c.charCodeAt(0), 0);
  const nConf = ff ? 0 : 1 + (seed % 2);
  const conflicts = ff ? [] : Array.from({ length: nConf }, (_, i) => pool[(seed + i * 7) % pool.length]!);
  return {
    ff,
    span: d.shared + d.aOnly + d.bOnly + 1,
    conflicts,
    nConf,
    mergeHash: hashFor(`${a}+${b}`),
    verdict: ff ? "fast-forward — no conflicts" : `${nConf} conflict${nConf > 1 ? "s" : ""} to resolve`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DB-tree geometry (ported from renderVals.gitView). A 600×600 viewBox so the
// lane Y + seq X math reads identically to the prototype.
// ─────────────────────────────────────────────────────────────────────────────

const TREE_VB = 600;
const LANE_Y = [140, 310, 468, 540];
const laneY = (lane: number): number => LANE_Y[lane] ?? 540;
const seqX = (seq: number): number => 150 + seq * 94;

interface TreeEdgePath {
  d: string;
  color: string;
  dash: string;
}

function commitEdgePaths(history: BranchHistory): TreeEdgePath[] {
  const byHash = new Map(history.commits.map((c) => [c.hash, c] as const));
  const branchByLane = new Map(history.branches.map((b) => [b.lane, b] as const));
  const edges: TreeEdgePath[] = [];
  for (const c of history.commits) {
    const cx = seqX(c.seq);
    const cy = laneY(c.lane);
    const br = branchByLane.get(c.lane);
    const color = br ? colorOfBranch(br) : "var(--v5-ink-3)";
    const dash = br && !br.merged ? "5 5" : "none";
    for (const ph of c.parents) {
      const p = byHash.get(ph);
      if (!p) continue;
      const px = seqX(p.seq);
      const py = laneY(p.lane);
      const d =
        py === cy
          ? `M${px},${py} L${cx},${cy}`
          : `M${px},${py} C${(px + cx) / 2},${py} ${(px + cx) / 2},${cy} ${cx},${cy}`;
      edges.push({ d, color, dash });
    }
  }
  return edges;
}

// ─────────────────────────────────────────────────────────────────────────────
// Small presentation helpers
// ─────────────────────────────────────────────────────────────────────────────

const VER_VIEW_DEFS: Array<{ key: VerView; label: string; title: string }> = [
  { key: "graph", label: "On the graph", title: "Version badges drawn on the DAG itself" },
  { key: "db", label: "Database tree", title: "The whole-DB git tree — branches, commits, merge" },
  { key: "artifact", label: "Artifact versions", title: "This artifact's linear version history" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function DagVersionsPanel({
  artifactId,
  artifactLabel,
  artifactVersions,
  branchHistory,
  view,
  onViewChange,
  defaultView = "artifact",
  className,
}: DagVersionsPanelProps) {
  const label = artifactLabel ?? artifactId;

  // ── Sub-view state (controlled or internal). ──
  const [internalView, setInternalView] = useState<VerView>(defaultView);
  const activeView = view ?? internalView;
  const setView = useCallback(
    (v: VerView) => {
      setInternalView(v);
      onViewChange?.(v);
    },
    [onViewChange],
  );

  // ── Artifact version history: real prop, else deterministic-from-id. ──
  const artifact = useMemo(() => {
    if (artifactVersions && artifactVersions.length > 0) {
      const det = deterministicVersionsFor(artifactId);
      return { versions: artifactVersions, stale: det.stale, inputChanged: det.inputChanged };
    }
    return deterministicVersionsFor(artifactId);
  }, [artifactId, artifactVersions]);

  // ── Branch/commit history: real prop, else the prototype tree. ──
  const history = useMemo<BranchHistory>(
    () => branchHistory ?? deterministicBranchHistory(),
    [branchHistory],
  );

  // ── Commit-compare A/B picker + merge preview (DB view only). Starts empty
  //    so the first click picks A and the second picks B (the design flow). ──
  const [cmpA, setCmpA] = useState<string | null>(null);
  const [cmpB, setCmpB] = useState<string | null>(null);
  const [showMerge, setShowMerge] = useState(false);

  const pickCommit = useCallback(
    (hash: string) => {
      setShowMerge(false);
      if (!cmpA || (cmpA && cmpB)) {
        // Start a fresh pair on the first / a third click.
        setCmpA(hash);
        setCmpB(null);
      } else if (hash === cmpA) {
        setCmpA(null);
      } else {
        setCmpB(hash);
      }
    },
    [cmpA, cmpB],
  );
  const clearCmp = useCallback(() => {
    setCmpA(null);
    setCmpB(null);
    setShowMerge(false);
  }, []);

  const diff = useMemo(() => commitDiff(history, cmpA, cmpB), [history, cmpA, cmpB]);
  const merge = useMemo(() => mergePreview(history, cmpA, cmpB), [history, cmpA, cmpB]);
  const hasDiff = !!(cmpA && cmpB);

  return (
    <div
      className={`v5-dag-versions${className ? ` ${className}` : ""}`}
      data-testid="v5-dag-versions"
      data-ver-view={activeView}
    >
      {/* ── Sub-view toggle (graph / db / artifact). ── */}
      <div className="v5-dag-ver-head">
        <span className="v5-dag-ver-title" data-testid="v5-dag-ver-title">
          {activeView === "db" ? "whole-DB branches & versions" : `${label} · versions`}
        </span>
        <span className="v5-dag-ver-toggle" role="group" aria-label="Versions view">
          {VER_VIEW_DEFS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={`v5-dag-ver-btn${activeView === v.key ? " is-active" : ""}`}
              title={v.title}
              aria-pressed={activeView === v.key}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </span>
      </div>

      {/* ── Body switches on the active sub-view. ── */}
      <div className="v5-dag-ver-body">
        {activeView === "artifact" ? (
          <ArtifactTimeline label={label} versions={artifact.versions} stale={artifact.stale} inputChanged={artifact.inputChanged} />
        ) : activeView === "db" ? (
          <DatabaseTree
            history={history}
            cmpA={cmpA}
            cmpB={cmpB}
            onPick={pickCommit}
            onClear={clearCmp}
            diff={diff}
            merge={merge}
            hasDiff={hasDiff}
            showMerge={showMerge}
            onToggleMerge={() => setShowMerge((s) => !s)}
          />
        ) : (
          <GraphVersionNote label={label} versions={artifact.versions} stale={artifact.stale} inputChanged={artifact.inputChanged} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ARTIFACT view — the linear version-history timeline (selVersions).
// ─────────────────────────────────────────────────────────────────────────────

function staleNote(stale: boolean, inputChanged: string | null): string {
  return stale
    ? `current version built before its input changed (${inputChanged}) — 1 rebuild behind`
    : "current version is up to date with its inputs";
}

function ArtifactTimeline({
  label,
  versions,
  stale,
  inputChanged,
}: {
  label: string;
  versions: ArtifactVersion[];
  stale: boolean;
  inputChanged: string | null;
}) {
  return (
    <div className="v5-dag-ver-artifact" data-testid="v5-dag-ver-artifact">
      <div className="v5-dag-ver-artifact-head">
        <span className="v5-dag-ver-artifact-label">{label}</span>
        <span className="v5-dag-ver-artifact-count">
          {versions.length} version{versions.length === 1 ? "" : "s"}
        </span>
      </div>
      <ol className="v5-dag-ver-timeline" role="list" aria-label={`${label} version history`}>
        {versions.map((v, i) => {
          const isLast = i === versions.length - 1;
          const cur = !!v.current;
          const tone = cur ? (stale ? "stale" : "current") : "past";
          return (
            <li key={v.v} className={`v5-dag-ver-step is-${tone}`} data-current={cur ? "true" : "false"}>
              <span className="v5-dag-ver-dot" aria-hidden="true" />
              <span className="v5-dag-ver-v">{v.v}</span>
              <span className="v5-dag-ver-date">{v.date}</span>
              {cur ? (
                <span className="v5-dag-ver-badge">{stale ? "current · stale" : "current"}</span>
              ) : null}
              {!isLast ? <span className="v5-dag-ver-link" aria-hidden="true" /> : null}
            </li>
          );
        })}
      </ol>
      <p className={`v5-dag-ver-note${stale ? " is-stale" : ""}`} role="note">
        {staleNote(stale, inputChanged)}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GRAPH view — the on-DAG caption (the explorer draws version badges on the
// nodes; this panel pairs that with the per-artifact summary).
// ─────────────────────────────────────────────────────────────────────────────

function GraphVersionNote({
  label,
  versions,
  stale,
  inputChanged,
}: {
  label: string;
  versions: ArtifactVersion[];
  stale: boolean;
  inputChanged: string | null;
}) {
  return (
    <div className="v5-dag-ver-graph" data-testid="v5-dag-ver-graph">
      <p className="v5-dag-ver-graph-lead">
        Version badges are drawn on the DAG — each node shows{" "}
        <span className="v5-dag-ver-chip">v{versions.length}</span>, and stale nodes are flagged.
      </p>
      <div className="v5-dag-ver-graph-row">
        <span className="v5-dag-ver-graph-label">{label}</span>
        <span className={`v5-dag-ver-chip${stale ? " is-stale" : ""}`}>
          v{versions.length}
          {stale ? " · stale" : ""}
        </span>
      </div>
      <p className={`v5-dag-ver-note${stale ? " is-stale" : ""}`} role="note">
        {staleNote(stale, inputChanged)}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DB view — the whole-DB git tree + the commit-compare A/B picker + diff +
// merge preview (gitView / gitDiff / gitMerge).
// ─────────────────────────────────────────────────────────────────────────────

function DatabaseTree({
  history,
  cmpA,
  cmpB,
  onPick,
  onClear,
  diff,
  merge,
  hasDiff,
  showMerge,
  onToggleMerge,
}: {
  history: BranchHistory;
  cmpA: string | null;
  cmpB: string | null;
  onPick: (hash: string) => void;
  onClear: () => void;
  diff: CommitDiff | null;
  merge: MergePreview | null;
  hasDiff: boolean;
  showMerge: boolean;
  onToggleMerge: () => void;
}) {
  const edges = useMemo(() => commitEdgePaths(history), [history]);
  const branchByLane = useMemo(
    () => new Map(history.branches.map((b) => [b.lane, b] as const)),
    [history.branches],
  );
  const byHash = useMemo(
    () => new Map(history.commits.map((c) => [c.hash, c] as const)),
    [history.commits],
  );

  // Synthesized merge ghost commit (placed to the right of A & B on lane 0).
  const mergeGhost = useMemo(() => {
    if (!showMerge || !cmpA || !cmpB || !merge) return null;
    const a = byHash.get(cmpA);
    const b = byHash.get(cmpB);
    if (!a || !b) return null;
    const ax = seqX(a.seq);
    const ay = laneY(a.lane);
    const bx = seqX(b.seq);
    const by = laneY(b.lane);
    const mx = Math.max(ax, bx) + 92;
    const my = 140;
    const color = merge.ff ? "var(--v5-primary)" : "var(--v5-warn)";
    const ghostEdges = [
      { ax, ay },
      { ax: bx, ay: by },
    ].map(({ ax: px, ay: py }) =>
      py === my
        ? `M${px},${py} L${mx},${my}`
        : `M${px},${py} C${(px + mx) / 2},${py} ${(px + mx) / 2},${my} ${mx},${my}`,
    );
    return { mx, my, color, edges: ghostEdges, hash: merge.mergeHash, label: merge.ff ? "merge (ff)" : "merge ⚠" };
  }, [showMerge, cmpA, cmpB, merge, byHash]);

  const pct = (v: number): string => `${(v / TREE_VB) * 100}%`;

  return (
    <div className="v5-dag-ver-db" data-testid="v5-dag-ver-db">
      {/* The git tree itself. */}
      <div className="v5-dag-tree" data-testid="v5-dag-tree">
        <svg
          className="v5-dag-tree-svg"
          viewBox={`0 0 ${TREE_VB} ${TREE_VB}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Database version tree"
        >
          {edges.map((e, i) => (
            <path
              key={`e-${i}`}
              className="v5-dag-tree-edge"
              d={e.d}
              fill="none"
              stroke={e.color}
              strokeWidth={2.5}
              strokeDasharray={e.dash}
              strokeLinecap="round"
            />
          ))}
          {mergeGhost
            ? mergeGhost.edges.map((d, i) => (
                <path
                  key={`me-${i}`}
                  className="v5-dag-tree-edge is-merge"
                  d={d}
                  fill="none"
                  stroke={mergeGhost.color}
                  strokeWidth={2.5}
                  strokeDasharray="4 4"
                  strokeLinecap="round"
                />
              ))
            : null}
        </svg>

        {/* Branch labels on the left rail. */}
        {history.branches.map((b) => (
          <div key={b.id} className="v5-dag-tree-branch" style={{ top: pct(laneY(b.lane)) }}>
            <span className="v5-dag-tree-branch-id" style={{ color: colorOfBranch(b) }}>
              {b.id}
            </span>
            <span className={`v5-dag-tree-branch-note${b.merged ? "" : " is-unmerged"}`}>
              {b.merged ? "merged" : "not in main"}
            </span>
          </div>
        ))}

        {/* Commit dots (clickable → A/B compare). */}
        {history.commits.map((c) => {
          const br = branchByLane.get(c.lane);
          const color = br ? colorOfBranch(br) : "var(--v5-ink-3)";
          const isA = c.hash === cmpA;
          const isB = c.hash === cmpB;
          const role = isA ? "a" : isB ? "b" : c.current ? "current" : "idle";
          return (
            <button
              key={c.hash}
              type="button"
              className={`v5-dag-commit is-${role}`}
              data-testid="v5-dag-commit"
              data-hash={c.hash}
              data-pick={isA ? "a" : isB ? "b" : ""}
              style={{ left: pct(seqX(c.seq)), top: pct(laneY(c.lane)) }}
              title={`${c.name} · ${c.hash} · ${c.date}`}
              aria-pressed={isA || isB}
              onClick={() => onPick(c.hash)}
            >
              <span
                className={`v5-dag-commit-dot${c.current ? " is-current" : ""}`}
                style={{
                  borderColor: color,
                  background: isA || isB || c.current ? color : "var(--v5-surface)",
                }}
                aria-hidden="true"
              />
              {isA || isB ? <span className={`v5-dag-commit-pick is-${isA ? "a" : "b"}`}>{isA ? "A" : "B"}</span> : null}
              <span className={`v5-dag-commit-name${c.current ? " is-current" : ""}`}>{c.name}</span>
              <span className="v5-dag-commit-hash">{c.hash}</span>
              <span className="v5-dag-commit-date">{c.date}</span>
              {c.tag ? <span className="v5-dag-commit-tag">{c.tag}</span> : null}
            </button>
          );
        })}

        {/* The synthesized merge ghost commit. */}
        {mergeGhost ? (
          <div
            className="v5-dag-commit is-merge"
            data-testid="v5-dag-merge-ghost"
            style={{ left: pct(mergeGhost.mx), top: pct(mergeGhost.my) }}
          >
            <span className="v5-dag-commit-dot is-merge" style={{ borderColor: mergeGhost.color }} aria-hidden="true" />
            <span className="v5-dag-commit-name is-current" style={{ color: mergeGhost.color }}>
              {mergeGhost.label}
            </span>
            <span className="v5-dag-commit-hash">{mergeGhost.hash}</span>
          </div>
        ) : null}

        {!hasDiff ? <div className="v5-dag-tree-hint">click two commits to diff →</div> : null}
      </div>

      {/* The commit-diff readout + merge preview. */}
      <div className="v5-dag-ver-diff" data-testid="v5-dag-ver-diff">
        {hasDiff && diff ? (
          <>
            <div className="v5-dag-diff-pair">
              <span className="v5-dag-diff-a">A</span>
              <span className="v5-dag-diff-name">{diff.aName}</span>
              <span className="v5-dag-diff-vs">vs</span>
              <span className="v5-dag-diff-b">B</span>
              <span className="v5-dag-diff-name">{diff.bName}</span>
            </div>
            <span className={`v5-dag-diff-compat is-${diff.compat === "compatible" ? "ok" : "warn"}`}>
              {diff.compat}
            </span>
            <p className="v5-dag-diff-rel">{diff.rel}</p>
            <div className="v5-dag-diff-stats">
              <div className="v5-dag-diff-stat">
                <span className="v5-dag-diff-stat-n">{diff.shared}</span>
                <span className="v5-dag-diff-stat-l">shared</span>
              </div>
              <div className="v5-dag-diff-stat is-a">
                <span className="v5-dag-diff-stat-n">{diff.aOnly}</span>
                <span className="v5-dag-diff-stat-l">A‑only</span>
              </div>
              <div className="v5-dag-diff-stat is-b">
                <span className="v5-dag-diff-stat-n">{diff.bOnly}</span>
                <span className="v5-dag-diff-stat-l">B‑only</span>
              </div>
            </div>
            <p className="v5-dag-diff-base">
              merge base · <span>{diff.baseName}</span>
            </p>
            <div className="v5-dag-diff-actions">
              <button
                type="button"
                className="v5-dag-merge-btn"
                aria-pressed={showMerge}
                onClick={onToggleMerge}
              >
                {showMerge ? "hide merge preview" : "preview merge →"}
              </button>
              <button type="button" className="v5-dag-clear-btn" onClick={onClear}>
                clear
              </button>
            </div>
            {showMerge && merge ? (
              <div className="v5-dag-merge" data-testid="v5-dag-merge">
                <div className="v5-dag-merge-verdict-row">
                  <span className={`v5-dag-merge-hash is-${merge.ff ? "ok" : "warn"}`}>merge {merge.mergeHash}</span>
                  <span className={`v5-dag-merge-verdict is-${merge.ff ? "ok" : "warn"}`}>{merge.verdict}</span>
                </div>
                <p className="v5-dag-merge-span">spans {merge.span} commits across both branches</p>
                {merge.conflicts.length > 0 ? (
                  <ul className="v5-dag-merge-conflicts" role="list">
                    {merge.conflicts.map((cf) => (
                      <li key={cf} className="v5-dag-merge-conflict">
                        <span className="v5-dag-merge-conflict-dot" aria-hidden="true" />
                        {cf}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <p className="v5-dag-diff-hint" role="note">
            {cmpA ? "commit A picked — click another commit to compare." : "click any two commits in the tree to diff their lineages."}
          </p>
        )}
      </div>
    </div>
  );
}

export default DagVersionsPanel;
